import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import DarkModeBtn from './DarkModeBtn';
import LogoutButton from './LogoutButton';
import { useUser } from '../contexts/UserContext';
const UserActionsBar = () => {
   const { userEmail, userName } = useUser();
   const [kycValidated, setKycValidated] = useState(false);
   const [userDetails, setUserDetails] = useState({
      accountNumber: '',
      memberSince: '',
      countryName: ''  // Solo guardamos el nombre del país
   });
   const [dropdownOpen, setDropdownOpen] = useState(false);
   const dropdownRef = useRef(null);
   // Cerrar el dropdown cuando se hace clic fuera
   useEffect(() => {
      const handleClickOutside = (event) => {
         if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setDropdownOpen(false);
         }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
         document.removeEventListener('mousedown', handleClickOutside);
      };
   }, [dropdownRef]);
   // Efecto para verificar el estado KYC del usuario y obtener sus datos
   useEffect(() => {
      const fetchUserData = async () => {
         try {
            const jwt = localStorage.getItem('jwt');
            const email = localStorage.getItem('userEmail');
            if (!jwt || !email) return;
            const response = await fetch('https://pt-api.digit-a.net/api/users/get-user-by-email', {
               method: 'POST',
               headers: {
                  'Authorization': `Bearer ${jwt}`,
                  'Content-Type': 'application/json'
               },
               body: JSON.stringify({ email })
            });
            const data = await response.json();
            if (data && data.user) {
               // Verificar si KYC está validado
               setKycValidated(!!data.user.kycValidated);
               // Buscar el país en la ubicación correcta: steps.yourDetails.country
               let countryName = 'United States'; // Valor por defecto
               if (data.user.steps?.yourDetails?.country) {
                  countryName = data.user.steps.yourDetails.country;
               } else if (data.user.steps?.kyc?.country) {
                  countryName = data.user.steps.kyc.country;
               } else if (data.user.country) {
                  countryName = data.user.country;
               }
               // Obtener datos del usuario
               setUserDetails({
                  accountNumber: data.user._id || '123456789',
                  memberSince: formatDate(data.user.createdAt) || '01/07/2025',
                  countryName: countryName
               });
            }
         } catch (error) {
            console.error("Error al verificar estado KYC:", error);
         }
      };
      fetchUserData();
   }, []);
   // Función para formatear fecha
   const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
         day: '2-digit',
         month: '2-digit',
         year: 'numeric'
      }).replace(/\//g, '/');
   };
   // Función inteligente para inferir el código de país desde su nombre
   const getCountryCodeFromName = (countryName) => {
      // Lista de todos los países del mundo
      const allCountryCodes = [
         'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT',
         'AU', 'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI',
         'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY',
         'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
         'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM',
         'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK',
         'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL',
         'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
         'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR',
         'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN',
         'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS',
         'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
         'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW',
         'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP',
         'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM',
         'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
         'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM',
         'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF',
         'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW',
         'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
         'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW'
      ];
      try {
         // Primero manejamos algunos casos especiales comunes
         if (countryName === 'United States' || countryName === 'USA' || countryName === 'Estados Unidos') {
            return 'us';
         }
         if (countryName === 'United Kingdom' || countryName === 'UK' || countryName === 'Reino Unido') {
            return 'gb';
         }
         // Para el resto de países, intentamos encontrar el código buscando el nombre en diferentes idiomas
         const languages = ['en', 'es', 'fr', 'de'];
         for (const code of allCountryCodes) {
            for (const lang of languages) {
               try {
                  const regionNames = new Intl.DisplayNames([lang], { type: 'region' });
                  const regionName = regionNames.of(code);
                  // Si el nombre del país coincide con el nombre de la región, devolvemos el código
                  if (regionName.toLowerCase() === countryName.toLowerCase()) {
                     return code.toLowerCase();
                  }
                  // Verificar también si el nombre del país está incluido en el nombre de la región
                  if (regionName.toLowerCase().includes(countryName.toLowerCase()) ||
                     countryName.toLowerCase().includes(regionName.toLowerCase())) {
                     return code.toLowerCase();
                  }
               } catch (e) {
                  // Ignorar errores y continuar con el siguiente idioma
               }
            }
         }
         // Si no se encuentra, intentamos extraer las dos primeras letras del nombre
         // Esto es una aproximación que funciona para muchos países: España -> ES, Venezuela -> VE
         if (countryName && countryName.length >= 2) {
            return countryName.slice(0, 2).toLowerCase();
         }
         // Si no se encuentra ninguna coincidencia, devolvemos 'un' (bandera de Naciones Unidas)
         return 'un';
      } catch (error) {
         console.error("Error al obtener el código de país:", error);
         return 'un'; // Devolvemos UN (Naciones Unidas) como fallback
      }
   };
   const toggleDropdown = () => {
      setDropdownOpen(!dropdownOpen);
   };
   return (
      <div className="ppt_user_actions flex-row-reverse flex-lg-row">
         <div className="dropdown ppt_user_actions_dropdown" ref={dropdownRef}>
            <span
               className="ppt_username dropdown-toggle"
               title={userEmail || 'Usuario'}
               onClick={toggleDropdown}
               role="button"
               aria-expanded={dropdownOpen}
               style={{ cursor: 'pointer' }}
            >
               <img
                  src={process.env.PUBLIC_URL + '/assets/img/icon_user.svg'}
                  data-lightmodeimg
                  alt="ProperT User"
                  border="0"
               />
               <span className="d-flex flex-lg-column gap-2 gap-lg-0">
                  {userName || userEmail || 'Usuario'}
                  {kycValidated && (
                     <img
                        src={process.env.PUBLIC_URL + '/assets/img/icon_verified.svg'}
                        alt="ProperT User Verified"
                        border="0"
                        width={'85px'}
                     />
                  )}
               </span>
            </span>
            {/* Dropdown Menu */}
            <div className={`dropdown-menu p-4 ${dropdownOpen ? 'show' : ''}`}
               style={{
                  minWidth: '250px',
                  right: '-10px',
                  top: '36px',
                  left: 'auto',
                  marginTop: '10px',
                  boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                  borderRadius: '16px',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
               }}>
               <div className="user-details">
                  <div className="mb-2">
                     <span className="fw-bold" style={{ color: "#013220" }}>Account Number:</span>
                     <p className="mb-0">{userDetails.accountNumber}</p>
                  </div>
                  <div className="mb-2">
                     <span className="fw-bold" style={{ color: "#013220" }}>Email:</span>
                     <p className="mb-0">{userEmail}</p>
                  </div>
                  <div className="mb-2">
                     <span className="fw-bold" style={{ color: "#013220" }}>Member since:</span>
                     <p className="mb-0">{userDetails.memberSince}</p>
                  </div>
                  <div className='d-flex align-items-center gap-3'>
                     <span className="fw-bold" style={{ color: "#013220" }}>Country</span>
                     <div className="d-flex align-items-center">
                        {userDetails.countryName && (
                           <div className="d-flex align-items-center">
                              {/* Uso de Circle Flags API con código derivado del nombre */}
                              <img
                                 src={`https://hatscripts.github.io/circle-flags/flags/${getCountryCodeFromName(userDetails.countryName)}.svg`}
                                 alt={`${userDetails.countryName} Flag`}
                                 style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    objectFit: 'cover'
                                 }}
                                 onError={(e) => {
                                    // Si la bandera no se encuentra, usamos la de la ONU
                                    e.target.src = `https://hatscripts.github.io/circle-flags/flags/un.svg`;
                                 }}
                              />
                              {/* <span className="ms-2">{userDetails.countryName}</span> */}
                           </div>
                        )}
                     </div>
                  </div>
               </div>
            </div>
         </div>
         <DarkModeBtn />
         <span className="d-flex align-items-center gap-3">
            <NavLink to="/settings" className="d-none d-lg-block">
               <img
                  src={process.env.PUBLIC_URL + '/assets/img/icon_settings.svg'}
                  data-lightmodeimg
                  alt="ProperT User"
                  border="0"
               />
            </NavLink>
            <span className="d-none d-lg-block">
               <LogoutButton />
            </span>
         </span>
      </div>
   );
};
export default UserActionsBar;